/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useMemo, useState } from "react";

import { dayjs } from "@fllite/ui/utils";

import { useDashboardBusinessCases } from "@/hooks/useDashboardBusinessCases";
import { useOperatorDashboardStats } from "@/hooks/useOperatorDashboardStats";

import { BusinessCaseColumn } from "./BusinessCaseColumn";
import { MetricsSection } from "./MetricsSection";
import { StatusTimeline } from "./StatusTimeline";
import { DashboardPeriod } from "./types";

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

// Pulls the last leg's arrival moment off either a booked trip's proposal
// legs or — if the proposal isn't filled in yet — the inquiry legs as a
// best-effort fallback. Returns null when we have no usable timestamp,
// which we treat as "include the row" downstream (better to over-show than
// silently drop an unknown trip).
const getLastLegArrivalMoment = (bc: any) => {
  const proposalLegs: any[] | undefined = bc?.trip?.proposal?.proposalLegs;
  const inquiryLegs: any[] | undefined = bc?.inquiry?.inquiryLegs;
  const legs =
    proposalLegs && proposalLegs.length > 0 ? proposalLegs : inquiryLegs;
  if (!legs || legs.length === 0) return null;
  const last = legs[legs.length - 1];
  const at = last?.arrivalAt ?? last?.departureAt;
  if (!at) return null;
  const tz =
    last?.arrivalTimeZone ??
    last?.destinationAirport?.timeZone ??
    last?.departureTimeZone ??
    last?.departureAirport?.timeZone;
  // dayjs.tz normalises the wall-clock string + zone into an absolute
  // instant, so the .isAfter(now) comparison is timezone-correct regardless
  // of whether the API returns UTC ISO strings or local-at-airport strings.
  return tz ? dayjs.tz(at, tz) : dayjs(at);
};

const ACTION_NEEDED_EXEMPT_STATUSES = new Set(["awaiting_payment", "paid"]);

// Statuses that represent a cancelled / archived trip — these should never
// appear in "Upcoming departures" even if the BC still has a future
// departureAt on its inquiry or proposal legs. `ai_rtp_trip_archived` is the
// AI-RTP flow's equivalent of `canceled`.
const CANCELLED_STATUSES = ["canceled", "ai_rtp_trip_archived"];

export const Dashboard: FC = () => {
  const [period, setPeriod] = useState<DashboardPeriod>("week");
  const { stats, isLoading: isStatsLoading } = useOperatorDashboardStats(period);

  const { lookbackStart } = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    // Reach back 2 days so multi-leg trips that have already departed but
    // not yet arrived are still picked up as "upcoming / in progress".
    const lookback = new Date(startOfToday);
    lookback.setDate(lookback.getDate() - 2);
    return {
      lookbackStart: toIsoDate(lookback),
    };
  }, []);

  // No upper bound on the departure window — show every upcoming departure,
  // not just the next 7 days. Capped at a generous page size so the list
  // stays bounded for operators with a very large pipeline.
  // `contractSignedOnly` keeps this card to confirmed bookings: the AI/RTP flow
  // sets departureAt at trip-creation now, so an unsigned trip would otherwise
  // land here. The gate belongs on this request, not on the shared collection —
  // /trips uses the same endpoint and must still show the whole funnel.
  const { data: rawUpcoming, isLoading: isUpcomingLoading } =
    useDashboardBusinessCases({
      departureAfter: lookbackStart,
      orderBy: "departureAt",
      orderDirection: "asc",
      itemsPerPage: 50,
      notStatus: CANCELLED_STATUSES,
      contractSignedOnly: true,
    });

  // Drop any rows whose last leg has already arrived — the backend window
  // pulls them in to catch in-progress trips, but we only want the ones
  // that are still ahead of "now".
  const upcoming = useMemo(() => {
    const now = dayjs();
    return rawUpcoming.filter((bc: any) => {
      const arrival = getLastLegArrivalMoment(bc);
      if (!arrival) return true;
      return arrival.isAfter(now);
    });
  }, [rawUpcoming]);

  return (
    <>
      <MetricsSection
        stats={stats}
        isLoading={isStatsLoading}
        period={period}
        onPeriodChange={setPeriod}
      />
      <StatusTimeline />
      <BusinessCaseColumn
        title="Upcoming departures"
        metaLabel="Departure"
        metaDate={(bc) => bc.departureAt ?? bc.inquiry?.inquiryLegs?.[0]?.departureAt}
        data={upcoming}
        isLoading={isUpcomingLoading}
        emptyMessage="No upcoming departures scheduled."
        showActionNeededBadge={(bc) =>
          !!bc?.status && !ACTION_NEEDED_EXEMPT_STATUSES.has(bc.status)
        }
      />
    </>
  );
};
